import { formatIsoInstant } from './timeGrid'

// HistoryQuery (#207 §2, §8.1): the filters of the «Correction history» tab and the body of POST dataCorrection/search.
//   Machine ⇒ `deviceId`, Worker ⇒ `workerId` (the CORRECTED worker), Status All / Applied / Reverted ⇒ no `status` /
//   ["APPLIED"] / ["REVERTED"], Search ⇒ `text`. Every key is optional and `{}` is page 0 of 20, so only what narrows goes in.
// «More filters» (story 211, RN1-RN3): Type ⇒ `kind: [one]`, Corrected stretch from/to ⇒ `window` (it overlaps, #207 RN20) and
//   Requested from/to ⇒ `requestedAt`. The dates are LOCAL days `yyyy-MM-dd` ('' = no limit): «from» is 00:00 local of that
//   day and «to» is inclusive, 00:00 local of the next day, both sent as ISO UTC with `Z`. There is no filter by
//   fabrication order nor by job (RN2, D-S4): «Search» already finds them.

export const HISTORY_PAGE_SIZE = 20
// Sun's limit for `text` after trimming (#207 §2, MAX_TEXT): longer is a 400, so the field stops there.
export const HISTORY_TEXT_MAX = 255
export const HISTORY_SEARCH_DEBOUNCE_MS = 300

export const STATUS_FILTERS = [
  { value: 'ALL', label: 'All corrections', status: [] },
  { value: 'APPLIED', label: 'Applied corrections', status: ['APPLIED'] },
  { value: 'REVERTED', label: 'Reverted corrections', status: ['REVERTED'] }
]

/** RN3 [D-S6]: a single choice; «Close now» lists only the closes (their trims are ADJUST_END or VOID). */
export const KIND_FILTERS = [
  { value: 'ALL', label: 'All types' },
  { value: 'ADJUST_START', label: 'Adjust start' },
  { value: 'ADJUST_END', label: 'Adjust end' },
  { value: 'VOID', label: 'Void' },
  { value: 'CLOSE_NOW', label: 'Close now' }
]

/** The two date ranges of «More filters», by the key of the body they fill. */
export const DATE_RANGES = [
  { name: 'window', label: 'Corrected stretch', fromLabel: 'Corrected stretch from', toLabel: 'Corrected stretch to' },
  { name: 'requestedAt', label: 'Requested', fromLabel: 'Requested from', toLabel: 'Requested to' }
]

export const CROSSED_DATES_KEY = '«From» cannot be later than «to».'

export const initialHistoryFilters = () => ({
  deviceId: '',
  workerId: '',
  status: 'ALL',
  text: '',
  kind: 'ALL',
  window: { fromDay: '', toDay: '' },
  requestedAt: { fromDay: '', toDay: '' }
})

const positiveId = (value) => {
  const id = Number(value)
  return Number.isInteger(id) && id >= 1 ? id : 0
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * 00:00 LOCAL of the day `yyyy-MM-dd` plus `days` days, in ms. Built from the local calendar, never by adding 24 h, so the day
 * of a clock change lasts 23 or 25 hours (impacto.md R8). `NaN` when it is not a day.
 */
export const localMidnightOf = (day, days = 0) => {
  const match = DAY.exec(day || '')
  if (!match) return Number.NaN
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days).getTime()
}

/**
 * The `yyyy-MM-dd` of a local date, '' when there is none or it is not valid. A year of fewer than four digits is a date still
 * being typed («12/10/2», «12/10/20»…): it is not a day yet, so it asks nothing (A211-7, S69).
 */
export const dayOf = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime()) || date.getFullYear() < 1000) return ''
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** `{from, to}` of a range of days: only the ends that have a value («to» inclusive), `undefined` when it has none. */
const rangeBody = ({ fromDay, toDay }) => {
  const range = {}
  const fromMs = localMidnightOf(fromDay)
  const toMs = localMidnightOf(toDay, 1)
  if (Number.isFinite(fromMs)) range.from = formatIsoInstant(fromMs)
  if (Number.isFinite(toMs)) range.to = formatIsoInstant(toMs)
  return Object.keys(range).length > 0 ? range : undefined
}

/** RN1: «from» later than «to» in a range is an error next to the field; with any of them nothing is searched. */
export const dateErrors = (filters) => {
  const errors = {}
  DATE_RANGES.forEach(({ name }) => {
    const { fromDay, toDay } = filters[name]
    if (fromDay && toDay && fromDay > toDay) errors[name] = { key: CROSSED_DATES_KEY, params: {} }
  })
  return errors
}

export const hasDateErrors = (filters) => Object.keys(dateErrors(filters)).length > 0

/** The body of POST dataCorrection/search for `filters` and the zero-based `page`. */
export const searchBody = (filters, page = 0) => {
  const body = {}
  const deviceId = positiveId(filters.deviceId)
  const workerId = positiveId(filters.workerId)
  if (deviceId) body.deviceId = deviceId
  if (workerId) body.workerId = workerId
  const statusFilter = STATUS_FILTERS.find((candidate) => candidate.value === filters.status)
  if (statusFilter && statusFilter.status.length > 0) body.status = statusFilter.status
  const text = (filters.text || '').trim()
  if (text !== '') body.text = text.slice(0, HISTORY_TEXT_MAX)
  if (filters.kind && filters.kind !== 'ALL' && KIND_FILTERS.some((candidate) => candidate.value === filters.kind)) body.kind = [filters.kind]
  DATE_RANGES.forEach(({ name }) => {
    const range = filters[name] ? rangeBody(filters[name]) : undefined
    if (range) body[name] = range
  })
  if (page > 0) body.page = page
  return body
}

/** A filter changed: the new filters, always from page 0 (#207 §8.1). */
export const withFilter = (filters, name, value) => ({ ...filters, [name]: value })

/** One end (`fromDay` / `toDay`) of a date range changed. */
export const withDay = (filters, range, end, day) => ({ ...filters, [range]: { ...filters[range], [end]: day } })
